import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useSearch } from '@tanstack/react-router'
import {
  ApiError,
  type PasskeyChallenge,
  type SignInResult,
  type SmsChallenge,
} from '@mads/shared'
import { Logo } from '@/components/Logo'
import { describeDays } from '@/lib/duration'
import { assertPasskey, PasskeyCancelled, passkeysSupported } from '@/lib/passkeys'
import { useAuthProviders } from '@/lib/queries'
import { api, completeSignIn, useSessionStore } from '@/lib/session'

const GOOGLE_ICON = (
  <svg viewBox="0 0 48 48" width={18} height={18} aria-hidden="true">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
)

/**
 * What the API's `?authError=` slugs mean, said in words.
 *
 * The slug travels rather than the sentence: the callback is an unauthenticated
 * redirect target, and a message read straight out of the query string is a
 * message an attacker could choose. These are the only ones this screen will
 * repeat.
 */
/** Said when Google's answer was no use to us, and when the slug is unfamiliar. */
const GOOGLE_FAILED = 'Google sign-in did not complete. Try again, or use your email and password.'

const AUTH_ERRORS: Record<string, string> = {
  'unknown-account': 'No account here uses that Google address. Accounts are set up by the Head of Tech.',
  inactive: 'This account has been deactivated.',
  'unverified-email':
    'Google has not verified the address on that account, so it cannot be used to sign in.',
  cancelled: 'Google sign-in was cancelled.',
  expired: 'That sign-in took too long to finish. Please try again.',
  throttled: 'Too many attempts. Please wait a few minutes and try again.',
  failed: GOOGLE_FAILED,
}

/**
 * Reads the token out of the fragment Google's callback sends us back with —
 * `#token=…`. It rides there rather than in the query string because fragments
 * are never sent to a server, so it stays out of the access logs it would
 * otherwise pass through on the way here.
 */
function tokenFromHash(hash: string): string | null {
  const token = new URLSearchParams(hash.replace(/^#/, '')).get('token')

  return token === null || token === '' ? null : token
}

/**
 * The second half of a sign-in, whichever kind the API asked for. Not a token
 * on either side: the API mints one only once this is answered.
 */
type SecondStep = Exclude<SignInResult, { mfa: null }>

/**
 * Email-and-password sign-in (spec §2), and "Continue with Google" beside it
 * (issue #13). There is no open sign-up either way — accounts are provisioned
 * by an admin on the Users screen, and Google identifies people rather than
 * enrolling them. Apple was dropped from the design (issue #12).
 *
 * The Google button stays disabled where no OAuth client is configured, with
 * the reason beside it; nothing here pretends to sign anyone in.
 */
export function SignIn() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Set when the password was right and the account has a second factor — a
  // passkey (issue #15), or a texted code (issue #16). Its presence is what
  // swaps the form for the second step, and which kind decides which step;
  // there is no token behind either, on this side or the server's.
  const [secondStep, setSecondStep] = useState<SecondStep | null>(null)

  const setToken = useSessionStore((s) => s.setToken)
  const navigate = useNavigate()

  const providers = useAuthProviders()
  const googleReady = providers.data?.google === true

  const { authError } = useSearch({ from: '/sign-in' })
  const hash = useLocation({ select: (location) => location.hash })

  // Coming back from Google with a token: verify it, land the session, and go
  // on into the app — the same finish the password form makes, from a token
  // somebody else issued.
  useEffect(() => {
    const token = tokenFromHash(hash)

    if (token === null) return

    let cancelled = false
    setBusy(true)

    completeSignIn(token)
      .then(() => {
        // Navigating away takes the fragment with it, so the token does not sit
        // in the address bar or in the history entry behind it.
        if (!cancelled) navigate({ to: '/' })
      })
      .catch(() => {
        if (!cancelled) {
          setError(GOOGLE_FAILED)
          setBusy(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [hash, navigate])

  // A failed Google sign-in comes back as a redirect, not as a rejected
  // promise, so the reason arrives in the URL.
  useEffect(() => {
    if (authError !== undefined) setError(AUTH_ERRORS[authError] ?? GOOGLE_FAILED)
  }, [authError])

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)

    try {
      const result = await api.login(email, password)

      if (result.mfa !== null) {
        setSecondStep(result)
        setBusy(false)
        return
      }

      // Fetched through `completeSignIn`, which carries the token explicitly.
      // The shared `api` reads its token from the store, and the store cannot
      // have it yet: it is landed together with the session, in one update, so
      // that `useRestoreSession` sees them both and neither flashes the splash
      // screen nor fetches the session a second time. Calling `api.session()`
      // here instead sends the request with no Authorization header at all, and
      // the 401 that comes back reads exactly like a wrong password.
      await completeSignIn(result.token)
      // Explicit, since without the splash/remount the sign-in route's
      // `beforeLoad` no longer re-runs to do it.
      navigate({ to: '/' })
    } catch (caught) {
      const message =
        caught instanceof ApiError && caught.status === 401
          ? 'That email and password did not match an active account.'
          : caught instanceof Error
            ? caught.message
            : 'Sign-in failed.'
      setError(message)
      setToken(null)
    } finally {
      setBusy(false)
    }
  }

  if (secondStep !== null) {
    const onDone = () => navigate({ to: '/' })
    // Back to the password with the reason above it. Used where the challenge
    // is gone from the API's side and there is nothing left here to retry.
    const onStartOver = (message: string | null) => {
      setSecondStep(null)
      setPassword('')
      setError(message)
    }

    return secondStep.mfa === 'passkey' ? (
      <PasskeyStep challenge={secondStep} onDone={onDone} onStartOver={onStartOver} />
    ) : (
      <SmsStep challenge={secondStep} onDone={onDone} onStartOver={onStartOver} />
    )
  }

  return (
    <AuthShell>
      <form className="auth-form" onSubmit={onSubmit}>
        <h1>Welcome back</h1>

        {error !== null && (
          <div className="error-note" role="alert">
            {error}
          </div>
        )}

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            className="input"
            type="email"
            autoComplete="username"
            required
            placeholder="you@madstheatre.org"
            style={{ minHeight: 46 }}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>

        <div className="field" style={{ marginBottom: 8 }}>
          <label htmlFor="password">Password</label>
          <div className="password-wrap">
            <input
              id="password"
              className="input"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              placeholder="••••••••"
              style={{ minHeight: 46, paddingRight: 58 }}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword((v) => !v)}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <Link to="/forgot-password" className="text-muted" style={{ fontSize: 13 }}>
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-block"
          style={{ minHeight: 48 }}
          disabled={busy}
        >
          {busy ? 'Signing in…' : 'Sign in'}
        </button>

        <div className="or-rule">or continue with</div>

        <div className="social-stack">
          {googleReady ? (
            // A plain link, not a button with a handler: what happens next is
            // a whole-page navigation to Google's consent screen, which is
            // what an anchor is for. The API answers it with a redirect.
            <a className="social-btn social-google" href="/api/auth/oauth/google/start">
              {GOOGLE_ICON}
              <span>Continue with Google</span>
            </a>
          ) : (
            <button type="button" className="social-btn social-google" disabled>
              {GOOGLE_ICON}
              <span>Continue with Google</span>
            </button>
          )}
        </div>
        {googleReady ? (
          <p className="social-hint">
            Use the same address your account is under — Google signs you in, it doesn&rsquo;t
            create an account.
          </p>
        ) : (
          <p className="social-hint">
            Social sign-in is available once an OAuth provider is configured.
          </p>
        )}

        <p className="social-hint">
          <Link to="/terms">Terms of use</Link>
          {' · '}
          <Link to="/privacy">Privacy</Link>
        </p>
      </form>
    </AuthShell>
  )
}

/** The plum brand panel and the column beside it, shared by both steps. */
function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="auth-split">
      <div className="auth-brand">
        <Logo layout="stacked" theme="white" size={52} />
      </div>

      <div className="auth-form-side">{children}</div>
    </div>
  )
}

/**
 * The second half of a sign-in, for an account with a passkey registered
 * (issue #15).
 *
 * The prompt fires on its own as soon as this appears: the person pressed
 * "Sign in" a moment ago and this is the same act continuing, not a new one to
 * be asked for. A dismissed prompt leaves the challenge untouched and simply
 * offers the button again — nothing was sent, so nothing was spent. Anything
 * the API turns down is different: it consumed the challenge saying so, and the
 * only way on from there is the password again.
 */
function PasskeyStep({
  challenge,
  onDone,
  onStartOver,
}: {
  challenge: PasskeyChallenge
  onDone: () => void
  onStartOver: (message: string | null) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const supported = passkeysSupported()
  // A ref, not the `busy` flag: two calls can be made in the same tick, before
  // any state update lands, and the browser answers the second one by throwing
  // "A request is already pending" — its own words, in front of somebody trying
  // to sign in. StrictMode's double-invoked effect does exactly that in
  // development, and so does an impatient second click.
  const inFlight = useRef(false)

  const prove = useCallback(async () => {
    if (inFlight.current) return

    inFlight.current = true
    setError(null)
    setBusy(true)

    try {
      const credential = await assertPasskey(challenge.publicKey)
      const token = await api.completePasskeySignIn(challenge.challengeToken, credential)

      await completeSignIn(token)
      onDone()
    } catch (caught) {
      if (caught instanceof PasskeyCancelled) {
        setError('No passkey was used. Try again, or start over.')
      } else if (caught instanceof ApiError) {
        // The challenge went with the refusal, so there is nothing here left to
        // try — back to the password with the API's own explanation.
        onStartOver(
          caught.status === 429
            ? 'Too many attempts. Please wait a few minutes and try again.'
            : caught.message,
        )
        return
      } else {
        // Whatever the browser threw, said in our words. Its own are written
        // for a developer reading a console.
        setError('That passkey could not be used. Try again, or start over.')
      }

      setBusy(false)
    } finally {
      inFlight.current = false
    }
  }, [challenge, onDone, onStartOver])

  // Once, on arrival. `prove` is stable for a given challenge, and the
  // challenge only ever changes by leaving this screen.
  useEffect(() => {
    if (supported) void prove()
  }, [supported, prove])

  return (
    <AuthShell>
      <div className="auth-form">
        <h1>One more step</h1>

        {error !== null && (
          <div className="error-note" role="alert">
            {error}
          </div>
        )}

        {supported ? (
          <>
            <p className="text-muted">
              This account is protected by a passkey. Confirm with the device or security key you
              registered.
            </p>

            <button
              type="button"
              className="btn btn-primary btn-block"
              style={{ minHeight: 48 }}
              onClick={() => void prove()}
              disabled={busy}
            >
              {busy ? 'Waiting for your passkey…' : 'Use your passkey'}
            </button>
          </>
        ) : (
          <p className="text-muted">
            This account is protected by a passkey, and this browser cannot use one. Open the
            inventory on a device you have registered a passkey on.
          </p>
        )}

        <button
          type="button"
          className="btn btn-secondary btn-block"
          style={{ marginTop: 'var(--space-3)' }}
          onClick={() => onStartOver(null)}
        >
          Back to sign in
        </button>
      </div>
    </AuthShell>
  )
}

/**
 * The second half of a sign-in for everybody without a passkey (issue #16): the
 * six digits Twilio has just texted to the number on the account.
 *
 * Nothing fires on its own here, unlike the passkey step — the code has to
 * arrive and be read off a phone first, so the person is doing something rather
 * than confirming something. A wrong code is not the end of the sign-in either:
 * the API keeps the challenge alive for a few goes, so a mistyped digit is
 * answered in place and the form is still there to correct. What *is* the end
 * is the challenge running out, which the API says in its own words above the
 * field, with "Back to sign in" underneath it.
 *
 * "Trust this device" is honoured only on a code that verifies, so ticking it
 * can never be a way to skip the step it is ticked on.
 */
function SmsStep({
  challenge,
  onDone,
  onStartOver,
}: {
  challenge: SmsChallenge
  onDone: () => void
  onStartOver: (message: string | null) => void
}) {
  const [code, setCode] = useState('')
  // Ticked to begin with, set by the project owner: most sign-ins are somebody
  // on their own machine, and asking them to opt in every time is how a
  // convenience nobody uses becomes a second step everybody resents.
  //
  // It is ticked, not hidden — this is an opt-out, so the wording beside it has
  // to do the work the default no longer does. It says the length in words, and
  // names the case where the answer should be no, because someone signing in on
  // the box-office machine is the person this default is wrong for and the only
  // one who can tell.
  const [trustDevice, setTrustDevice] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setBusy(true)

    try {
      const token = await api.completeSmsSignIn(challenge.challengeToken, code.trim(), trustDevice)

      await completeSignIn(token)
      onDone()
    } catch (caught) {
      // 429 is the one refusal there is no point staying for: the limit is on
      // the caller, not on this code, so another go in five seconds is another
      // 429. Everything else — a wrong code, an expired challenge, Twilio
      // briefly unreachable — is said in place, because the API's own sentence
      // already tells the person whether to retype or start again.
      if (caught instanceof ApiError && caught.status === 429) {
        onStartOver('Too many attempts. Please wait a few minutes and try again.')
        return
      }

      setError(
        caught instanceof Error ? caught.message : 'That code could not be checked. Try again.',
      )
      setBusy(false)
    }
  }

  return (
    <AuthShell>
      <form className="auth-form" onSubmit={onSubmit}>
        <h1>One more step</h1>

        {error !== null && (
          <div className="error-note" role="alert">
            {error}
          </div>
        )}

        <p className="text-muted">
          We&rsquo;ve texted a code to the number ending {challenge.phoneHint}. Enter it below to
          finish signing in.
        </p>

        <div className="field">
          <label htmlFor="sms-code">Verification code</label>
          <input
            id="sms-code"
            className="input"
            // `one-time-code` is what lets iOS and Android offer the code from
            // the message itself, which is the whole difference between this
            // being a moment and being a chore.
            autoComplete="one-time-code"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={10}
            required
            autoFocus
            placeholder="123456"
            style={{ minHeight: 46, letterSpacing: '0.25em' }}
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        </div>

        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-4)',
          }}
        >
          <input
            type="checkbox"
            style={{ marginTop: 4 }}
            checked={trustDevice}
            onChange={(event) => setTrustDevice(event.target.checked)}
          />
          <span>
            Trust this device
            <span className="text-muted" style={{ display: 'block', fontSize: 12 }}>
              Skip this step on this browser for {describeDays(challenge.trustDeviceDays)}. Your
              password is still needed every time. <strong>Untick this</strong> if this computer is
              shared, or isn&rsquo;t yours.
            </span>
          </span>
        </label>

        <button
          type="submit"
          className="btn btn-primary btn-block"
          style={{ minHeight: 48 }}
          disabled={busy || code.trim() === ''}
        >
          {busy ? 'Checking…' : 'Verify and sign in'}
        </button>

        <button
          type="button"
          className="btn btn-secondary btn-block"
          style={{ marginTop: 'var(--space-3)' }}
          onClick={() => onStartOver(null)}
        >
          Back to sign in
        </button>

        <p className="social-hint">
          No message? Go back and sign in again to have another one sent.
        </p>
      </form>
    </AuthShell>
  )
}
